import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Users, Vote, ArrowRight, MapPin, CheckSquare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Reveal } from "@/components/Reveal";
import { Countdown } from "@/components/Countdown";
import { Section, Eyebrow } from "@/components/common";
import { api } from "@/lib/api";
import { useContent } from "@/lib/useContent";
import { trackEvent } from "@/lib/analytics";

const HERO_IMAGES = ["/hero1v2.jpg", "/hero2.jpg", "/hero3.jpg"];

const SignupBand = () => {
  const [form, setForm] = useState({ email: "", phone: "", sms_consent: false });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/newsletter", form);
      trackEvent("form_submit", { form: "newsletter", location: "home" });
      toast.success("Thank you for joining the movement!");
      setForm({ email: "", phone: "", sms_consent: false });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="home-signup-form">
      <Input type="email" required placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/10 border-white/25 text-white placeholder:text-blue-200" data-testid="home-signup-email" />
      <Input type="tel" placeholder="Mobile phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white/10 border-white/25 text-white placeholder:text-blue-200" data-testid="home-signup-phone" />
      <label className="sm:col-span-2 flex items-start gap-2.5 text-xs text-blue-100 leading-relaxed cursor-pointer">
        <Checkbox checked={form.sms_consent} onCheckedChange={(v) => setForm({ ...form, sms_consent: !!v })} className="mt-0.5 border-white/40 data-[state=checked]:bg-brightblue" data-testid="home-signup-sms-consent" />
        <span>By providing your phone number, you consent to receive recurring campaign text messages from Friends of Satish Chapagain. Msg &amp; data rates may apply. Reply STOP to opt out.</span>
      </label>
      <Button type="submit" disabled={loading} className="sm:col-span-2 bg-brightblue hover:bg-brightblue-hover text-white font-semibold h-11" data-testid="home-signup-submit">
        {loading ? "Joining..." : "Join the Movement"}
      </Button>
    </form>
  );
};

export default function Home() {
  const { data: content } = useContent();
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_IMAGES.length), 5000);
    return () => clearInterval(id);
  }, []);

  if (!content) return <div className="h-screen" />;
  const home = content.home || {};
  const priorities = content.priorities || [];

  return (
    <div>
      {/* HERO */}
      <section className="relative hero-gradient text-white overflow-hidden" data-testid="home-hero">
        {HERO_IMAGES.map((src, i) => (
          <div key={src} className="absolute inset-0 transition-opacity duration-1000" style={{ opacity: i === heroIdx ? 1 : 0 }}>
            <img src={src} alt="Satish Chapagain with community members in District 7A" className="w-full h-full object-cover object-center" />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-navy-dark via-navy-dark/55 to-transparent" />
        <div className="absolute inset-0 bg-navy-dark/10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white/10 border border-white/20 text-blue-100 mb-6">
              Candidate for Maryland State Delegate &middot; District 7A
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]">
              {home.hero_headline || "People First. Community Always."}
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-blue-100 leading-relaxed max-w-2xl">{home.hero_subhead}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="bg-donate hover:bg-donate-hover text-white rounded-full font-semibold text-base h-12 px-7" data-testid="hero-donate-btn">
                <Link to="/donate"><Heart className="w-5 h-5 mr-2" /> Donate</Link>
              </Button>
              <Button asChild size="lg" className="bg-brightblue hover:bg-brightblue-hover text-white rounded-full font-semibold text-base h-12 px-7" data-testid="hero-volunteer-btn">
                <Link to="/get-involved"><Users className="w-5 h-5 mr-2" /> Volunteer</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full font-semibold text-base h-12 px-7 bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white" data-testid="hero-vote-btn">
                <Link to="/vote"><Vote className="w-5 h-5 mr-2" /> Make Your Plan to Vote</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* DONATE APPEAL BAND */}
      <Section className="pt-12">
        <Reveal className="bg-white border border-slate-200 rounded-2xl shadow-sm px-5 sm:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-center sm:text-left">
            <div className="w-9 h-9 rounded-full bg-donate/10 hidden sm:flex items-center justify-center shrink-0">
              <Heart className="w-4.5 h-4.5 text-donate" />
            </div>
            <div className="sm:flex sm:items-baseline sm:gap-2">
              <span className="font-serif text-base font-bold text-navy">Power this campaign</span>
              <span className="text-slate-600 text-sm block sm:inline">Chip in today — every dollar helps us reach more neighbors.</span>
            </div>
          </div>
          <Button asChild className="bg-donate hover:bg-donate-hover text-white rounded-full font-semibold h-10 px-6 shadow-md shadow-donate/20 hover:scale-105 transition-transform shrink-0" data-testid="home-appeal-donate-btn">
            <Link to="/donate"><Heart className="w-4 h-4 mr-2" /> Donate Now</Link>
          </Button>
        </Reveal>
      </Section>

      {/* COUNTDOWN */}
      <Section className="py-14">
        <Reveal className="bg-gradient-to-r from-blue-950 via-slate-900 to-blue-900 text-white rounded-3xl shadow-2xl px-6 sm:px-10 py-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <div className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-blue-300">Countdown to Election Day</div>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold mt-2">Tuesday, November 3, 2026</h2>
              <p className="text-blue-200 mt-2 text-sm">Every day counts. Make your plan to vote today.</p>
            </div>
            <Countdown target={content.election_day || "2026-11-03T00:00:00"} light />
          </div>
          <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <p className="text-blue-200 text-sm">Not sure where to vote?</p>
            <Button asChild className="bg-white text-navy hover:bg-blue-50 rounded-full font-semibold" data-testid="home-polling-place-btn">
              <a href="https://voterservices.elections.maryland.gov/PollingPlaceSearch" target="_blank" rel="noopener noreferrer">
                <MapPin className="w-4 h-4 mr-2" /> Find Your Polling Place
              </a>
            </Button>
          </div>
        </Reveal>
      </Section>

      {/* VOTE FOR SATISH — ballot appeal */}
      <Section className="pb-4">
        <Reveal className="hero-gradient text-white rounded-3xl shadow-xl overflow-hidden relative">
          <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-white/5" aria-hidden="true" />
          <div className="relative px-6 sm:px-10 py-9 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5 text-center sm:text-left">
              <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center shrink-0 shadow-lg">
                <CheckSquare className="w-9 h-9 text-brightblue" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-blue-300">This November</div>
                <h2 className="font-serif text-2xl sm:text-3xl font-bold mt-1">Vote for Satish Chapagain</h2>
                <p className="text-blue-100 text-sm mt-1">Maryland House of Delegates &middot; District 7A</p>
              </div>
            </div>
            <Button asChild size="lg" className="bg-white text-navy hover:bg-blue-50 rounded-full font-bold h-12 px-7 shadow-lg shrink-0" data-testid="home-vote-appeal-btn">
              <Link to="/vote"><Vote className="w-5 h-5 mr-2" /> Make Your Plan to Vote</Link>
            </Button>
          </div>
        </Reveal>
      </Section>

      {/* INTRO */}
      <Section className="py-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-5">
            <img src="/satish-portrait-45.jpg" alt="Satish Chapagain, candidate for Maryland State Delegate, District 7A" className="rounded-3xl shadow-xl w-full object-cover object-center aspect-[4/5]" data-testid="home-portrait" />
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <Eyebrow>A Letter From Satish</Eyebrow>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-navy tracking-tight">Why I'm running for our community</h2>
            <p className="mt-5 text-lg text-slate-700 leading-relaxed">{home.intro_full}</p>
            <div className="mt-6">
              <Button asChild variant="outline" className="border-navy/30 text-navy hover:bg-sky rounded-full font-semibold" data-testid="home-about-btn">
                <Link to="/about">Meet Satish <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* WHO SATISH IS */}
      <Section className="py-14">
        <Reveal className="text-center max-w-2xl mx-auto mb-10">
          <Eyebrow>Who Satish Is</Eyebrow>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-navy tracking-tight">A trustworthy neighbor ready to serve</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {(home.who_cards || []).map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 p-6" data-testid={`who-card-${i}`}>
              <div className="w-11 h-11 rounded-xl hero-gradient flex items-center justify-center text-white font-serif font-bold mb-4">{i + 1}</div>
              <h3 className="font-serif text-lg font-bold text-navy">{c.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.text}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* PRIORITIES PREVIEW */}
      <Section className="py-14">
        <Reveal className="text-center max-w-2xl mx-auto mb-10">
          <Eyebrow>What We're Fighting For</Eyebrow>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-navy tracking-tight">Our priorities for District 7A</h2>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {priorities.slice(0, 8).map((p, i) => (
            <Reveal key={p.title} delay={i * 0.05} className="bg-sky/60 rounded-2xl border border-sky-border p-5 text-center hover:bg-white hover:shadow-md transition-all" data-testid={`priority-preview-${i}`}>
              <div className="font-semibold text-navy text-sm">{p.title}</div>
            </Reveal>
          ))}
        </div>
        <div className="text-center mt-8">
          <Button asChild className="bg-brightblue hover:bg-brightblue-hover text-white rounded-full font-semibold" data-testid="home-priorities-btn">
            <Link to="/priorities">See All Priorities <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
          </Button>
        </div>
      </Section>

      {/* SIGNUP BAND */}
      <Section className="py-14">
        <Reveal className="bg-gradient-to-r from-blue-950 via-slate-900 to-blue-900 text-white rounded-3xl shadow-2xl px-6 sm:px-12 py-12">
          <div className="max-w-2xl mx-auto text-center mb-8">
            <Eyebrow><span className="text-blue-300">Join Us</span></Eyebrow>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold">Get campaign updates by email &amp; text</h2>
            <p className="mt-3 text-blue-100">Be the first to hear about events, volunteer opportunities, and voting info.</p>
          </div>
          <div className="max-w-xl mx-auto"><SignupBand /></div>
        </Reveal>
      </Section>

      {/* CLOSING CTA */}
      <Section className="pb-20">
        <div className="grid sm:grid-cols-2 gap-6">
          <Reveal className="rounded-3xl p-10 bg-white border border-slate-200 shadow-sm text-center">
            <Heart className="w-10 h-10 mx-auto text-donate" />
            <h3 className="font-serif text-2xl font-bold text-navy mt-4">Power this campaign</h3>
            <p className="text-slate-600 mt-2">Chip in today — every dollar helps us reach more neighbors.</p>
            <Button asChild className="mt-5 bg-donate hover:bg-donate-hover text-white rounded-full font-semibold" data-testid="closing-donate-btn">
              <Link to="/donate">Donate Now</Link>
            </Button>
          </Reveal>
          <Reveal delay={0.1} className="rounded-3xl p-10 hero-gradient text-white text-center shadow-sm">
            <Users className="w-10 h-10 mx-auto text-blue-200" />
            <h3 className="font-serif text-2xl font-bold mt-4">Join our team</h3>
            <p className="text-blue-100 mt-2">Knock doors, make calls, or host a meet &amp; greet.</p>
            <Button asChild className="mt-5 bg-brightblue hover:bg-brightblue-hover text-white rounded-full font-semibold" data-testid="closing-volunteer-btn">
              <Link to="/get-involved">Volunteer Today</Link>
            </Button>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
